import * as cadviewer from "cadviewer";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
  startTransition
} from "react";
import Scrollbar from "react-scrollbars-custom";
import SwipeableViews from "react-swipeable-views";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import useConfig from "../../demos/hooks/useConfig";
import { SpaceObjectProps } from "../../types";
import { SpaceInfo } from "../../types/plan";
import { classNames } from "../../utils/css";
import DisabledClickWrapper from "../DisabledClickWrapper";
import SpaceObjectInfo from "../SpaceObjectInfo";
import VersionDisplay from "../VersionDisplay/VersionDisplay.component";
import { useSpaceSelectionStore } from "../../modules/ai/stores/spaceSelectionStore";
import { markLeftPaneNavigation } from "../CADViewer/CADViewer";

// Interface for copied space data
interface CopiedSpaceData {
  spaceNumber: string;
  DBA: string;
}

// Global storage for copied space data
let copiedSpaceDataStore: CopiedSpaceData | null = null;

// Method to set copied space data
export const setCopiedSpaceData = (data: CopiedSpaceData | null | {}) => {
  copiedSpaceDataStore = data as CopiedSpaceData | null;
  // Also expose to window for CADViewer commands
  (window as any).copiedSpaceData = data;
};

// Method to get copied space data
export const getCopiedSpaceData = (): CopiedSpaceData | null => {
  return copiedSpaceDataStore;
};

interface SideBarLeftPanelProps {
  leftPanelOpen: boolean;
  setLeftPanelOpen: any;
}

const SideBarLeftPanel = ({ leftPanelOpen }: SideBarLeftPanelProps) => {
  const { selectedSpaceObjectID, selectedPlanDetail } =
    useContext(CadviewerContext);

  const { config } = useConfig();

  return (
    <aside
      className={
        "h-screen max-h-screen transform transition-transform ease-in duration-150 bg-sidebar-background flex flex-col overflow-hidden"
      }
    >
      {leftPanelOpen &&
        (true ? (
          <SwipeableViews
            className="h-screen max-h-screen overflow-hidden"
            index={
              config.ShowInformativeLeftPanel && selectedSpaceObjectID ? 1 : 0
            }
          >
            <div className="h-screen max-h-screen overflow-hidden">
              {selectedPlanDetail ? <PlanTenantList /> : <SpaceObjectList />}
            </div>
            <div className="h-screen max-h-screen overflow-hidden">
              <SpaceObjectInfo showInLeftPanel />
            </div>
          </SwipeableViews>
        ) : selectedPlanDetail ? (
          <PlanTenantList />
        ) : (
          <SpaceObjectList />
        ))}
      <div className="absolute bottom-0 left-0 right-0">
        <VersionDisplay className="mx-auto mb-2 text-center w-full text-white" />
      </div>
    </aside>
  );
};

const PlanTenantList = () => {
  const scrollContainerRef = useRef(null);
  const itemRefs = useRef<HTMLElement[]>([]);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [expanded, setExpanded] = useState<string | undefined>();
  const [visibleCount, setVisibleCount] = useState<number>(30);
  const {
    plansInfo,
    selectedSpaceObjectID,
    setSelectedSpaceObjectID,
    selectedPlan,
    multiSelectArray,
    disableFeature,
    isDataImportActive,
  } = useContext(CadviewerContext);
  const { config } = useConfig();

  // Get space selection state from Zustand store
  const { isSelectingSpaceForAI, spaceSelectedForAI } = useSpaceSelectionStore();

  // Check if we're in AI mode (either selecting or already selected a space for AI)
  const isInAIMode = isSelectingSpaceForAI || spaceSelectedForAI !== null;

  useEffect(() => {
    // Skip scroll behavior if we're in AI mode
    if (isInAIMode) {
      return;
    }

    if (selectedSpaceObjectID) {
      const spaceInfoIndex = plansInfo?.spaces.findIndex(
        (spaceInfo: SpaceInfo) =>
          selectedSpaceObjectID ===
          spaceInfo["spaceNumber"].replaceAll(".", "_").replaceAll(" ", "_")
      );
      if (spaceInfoIndex !== undefined && spaceInfoIndex !== -1) {
        const spaceInfo = plansInfo!.spaces[spaceInfoIndex];
        setExpanded(spaceInfo["spaceNumber"]);
        
        // Defer heavy list expansion so CADViewer interaction stays instant
        setTimeout(() => {
          startTransition(() => {
            setVisibleCount((prev) => Math.max(prev, spaceInfoIndex + 20));
          });
          
          // Delay DOM query and scroll to allow React to render newly visible items
          setTimeout(() => {
            const element =
              document.getElementById(
                "plan-info-item-" + spaceInfo["spaceNumber"]
              ) ||
              document.getElementById(
                "plan-item-" +
                  `${selectedSpaceObjectID}`
                    .replaceAll(".", "_")
                    .replaceAll(" ", "_")
              );

            if (element) {
              element.scrollIntoView({
                behavior: "smooth",
                block: "start",
                inline: "start",
              });
            }
          }, 300);
        }, 50);
      } else {
        console.warn(`Space ${selectedSpaceObjectID} not found in left panel list.`);
        setExpanded(undefined);
      }
    } else {
      setExpanded(undefined);
    }
  }, [plansInfo?.spaces, selectedSpaceObjectID, isInAIMode]);

  const filterSpaceInfos = useMemo(() => {
    return (plansInfo ?? { spaces: [] }).spaces.filter(
      (spaceInfo: SpaceInfo) => {
        return searchKeyword.trim() === ""
          ? true
          : (spaceInfo["spaceNumber"] ?? "")
              .toLowerCase()
              .includes(searchKeyword.toLowerCase()) ||
              (spaceInfo["spaceHeader"] ?? "")
                .toLowerCase()
                .includes(searchKeyword.toLowerCase()) ||
              Object.values(spaceInfo["summary"]).some((value: string) =>
                value.toLowerCase().includes(searchKeyword.toLowerCase())
              );
      }
    );
  }, [plansInfo, searchKeyword]);

  // reset keyword when plansInfo changes
  useEffect(() => {
    setSearchKeyword("");
    setVisibleCount(30);
  }, [plansInfo, selectedPlan]);

  useEffect(() => {
    setVisibleCount(30);
  }, [searchKeyword]);

  return (
    <div className="absolute top-0 left-0 right-0  bottom-0 max-h-screen overflow-hidden">
      <div
        className={`${
          config.showLogoOnSideBarLeftPanel ? "h-28" : "h-14"
        } absolute top-0 left-0 right-0`}
      >
        {config.showLogoOnSideBarLeftPanel && (
          <div className="flex h-14 bg-white py-2 px-2">
            <img
              src={"/CADViewer_Primary_logo.svg"}
              alt="CADViewer Logo"
              className={`h-9 sm:h-10`}
            />
          </div>
        )}
        <form
          className="bg-white/30 p-2 m-2 rounded-md relative flex flex-1"
          action="#"
          method="GET"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <label htmlFor="search-field" className="sr-only">
            Search
          </label>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="pointer-events-none absolute inset-y-0 left-2 h-full w-5 text-sidebar-title"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
            />
          </svg>

          <DisabledClickWrapper>
            <input
              id="search-field"
              className="bg-transparent block h-full w-full border-0 py-0 pl-8 pr-0 text-sidebar-title placeholder:text-sidebar-title/80 focus:ring-0 sm:text-sm outline-none  focus:outline-none"
              placeholder="Search..."
              type="text"
              name="search"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              disabled={disableFeature}
            />
          </DisabledClickWrapper>

          {searchKeyword && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              onClick={() => setSearchKeyword("")}
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="cursor-pointer absolute inset-y-0 right-2 h-full w-5 text-sidebar-title"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          )}
        </form>
      </div>
      <div
        className={`min-h-0 h-screen overflow-hidden ${
          config.showLogoOnSideBarLeftPanel ? "pt-28" : "pt-14"
        }`}
      >
        <div
          ref={scrollContainerRef}
          className="divide-y divide-white/5 overflow-y-auto h-full custom-scrollbar"
          style={{
            scrollSnapType: "y mandatory",
            scrollbarWidth: "thin",
          }}
          onScroll={(e: any) => {
            const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 2000;
            if (bottom && visibleCount < filterSpaceInfos.length) {
              setVisibleCount(Math.min(visibleCount + 30, filterSpaceInfos.length));
            }
          }}
        >
          {filterSpaceInfos.slice(0, visibleCount).map((spaceInfo: SpaceInfo, index: number) => {
            const isSelected = selectedSpaceObjectID === spaceInfo["spaceNumber"].replaceAll(".", "_").replaceAll(" ", "_");
            const isExpanded = expanded === spaceInfo["spaceNumber"];
            return (
            <div
              ref={(el: any) => (itemRefs.current[index] = el)}
              key={
                filterSpaceInfos.length +
                "-" +
                spaceInfo["spaceNumber"] +
                "-" +
                spaceInfo["spaceHeader"]
              }
            >
              <SpaceInfoItem
                spaceInfo={spaceInfo}
                setExpanded={setExpanded}
                isExpanded={isExpanded}
                index={index}
                selected={isSelected}
                setSelectedSpaceObjectID={setSelectedSpaceObjectID}
                disableFeature={disableFeature}
              />
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const SpaceInfoItem = memo(({
  spaceInfo,
  isExpanded,
  setExpanded,
  index,
  selected,
  setSelectedSpaceObjectID,
  disableFeature,
}: {
  spaceInfo: SpaceInfo;
  isExpanded: boolean;
  setExpanded: any;
  index: number;
  selected: boolean;
  setSelectedSpaceObjectID: any;
  disableFeature: boolean;
}) => {
  const { config } = useConfig();
  const { isDataImportActive, copiedSpaceId, setCopiedSpaceId } = useContext(CadviewerContext);

  const handleCopySpaceData = useCallback((spaceInfo: SpaceInfo) => {
    // If clicking the already selected space, unselect it
    if (copiedSpaceId === spaceInfo["spaceNumber"]) {
      setCopiedSpaceData({});
      setCopiedSpaceId(null);
      console.log("Unselected space data");
      return;
    }

    // Extract DBA from the summary object, fallback to detailTabs
    let dba = spaceInfo?.summary?.DBA || "";
    
    // If not found in summary, try finding it in detailTabs
    if (!dba && spaceInfo?.detailTabs) {
      const tabWithDba = spaceInfo.detailTabs.find(tab => tab.detail?.DBA);
      if (tabWithDba) {
        dba = tabWithDba.detail.DBA;
      }
    }

    // Create the data structure
    const copiedData: CopiedSpaceData = {
      spaceNumber: spaceInfo["spaceNumber"],
      DBA: dba
    };

    // Store the data using the global method
    setCopiedSpaceData(copiedData);
    
    // Highlight the selected storefront button
    setCopiedSpaceId(spaceInfo["spaceNumber"]);

    // Optional: Show visual feedback or console log
    console.log("Copied space data:", copiedData);
  }, [copiedSpaceId, setCopiedSpaceId]);

  const focusSpaceObject = useCallback(() => {
    markLeftPaneNavigation(spaceInfo["spaceNumber"].replaceAll(".", "_").replaceAll(" ", "_"));
    setExpanded((prev: string | undefined) =>
      prev === spaceInfo["spaceNumber"] ? undefined : spaceInfo["spaceNumber"]
    );
    cadviewer.cvjs_hideOnlyPop();

    if (config.ShowInformativeBottomPanelWhenClickInLeftPanel) {
      setSelectedSpaceObjectID(spaceInfo["spaceNumber"]);
    }

    cadviewer.cvjs_changeSpaceFixedLocation(
      spaceInfo["spaceNumber"].replaceAll(".", "_").replaceAll(" ", "_"),
      null
    );

    cadviewer.cvjs_zoomHere_ObjectId(
      spaceInfo["spaceNumber"].replaceAll(".", "_").replaceAll(" ", "_"),
      config.ZoomFactor
    );
  }, [
    setExpanded,
    config.ShowInformativeBottomPanelWhenClickInLeftPanel,
    config.ZoomFactor,
    spaceInfo,
    setSelectedSpaceObjectID,
  ]);

  return (
    <DisabledClickWrapper>
      <div
        data-index={index}
        key={spaceInfo["spaceNumber"] + "-" + spaceInfo["spaceHeader"]}
        id={"plan-info-item-" + spaceInfo["spaceNumber"]}
        className={classNames(
          "relative py-2 px-2",
          !disableFeature && "cursor-pointer hover:bg-sidebar-item-hover",
          "transition-colors duration-300 ease-in-out",
          // selected
          selected ? "bg-sidebar-item-hover/10" : ""
        )}
        style={{ scrollSnapAlign: "start" }}
        onClick={() => {
          if (!disableFeature) {
            focusSpaceObject();
          }
        }}
        onMouseEnter={() => {
          // setExpanded(true);
        }}
        onMouseLeave={() => {
          // setExpanded(false);
        }}
      >
        <div className="flex items-center space-x-4 ">
          <div className="min-w-0 flex-auto">
            <div className="flex items-center gap-x-3">
              <div
                className={classNames(
                  selected
                    ? "text-sidebar-active-item bg-green-400/10"
                    : "text-sidebar-subtitle bg-gray-100/10",
                  "flex-none rounded-full p-1"
                )}
              >
                <div className="h-2 w-2 rounded-full bg-current" />
              </div>
              <h2 className={classNames(
                "min-w-0 text-sm font-semibold leading-6 flex-1 text-ellipsis",
                copiedSpaceId === spaceInfo["spaceNumber"]
                  ? "text-btn-accent"
                  : "text-sidebar-title"
              )}>
                <span className=" text-ellipsis">
                  {spaceInfo["spaceHeader"]}
                </span>
              </h2>
              <div className={"flex items-center gap-x-2"}>
                {isDataImportActive && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopySpaceData(spaceInfo);
                    }}
                    className={classNames(
                      "transition-colors",
                      copiedSpaceId === spaceInfo["spaceNumber"]
                        ? "text-btn-accent hover:text-btn-accent-hover"
                        : "text-sidebar-subtitle hover:text-sidebar-title"
                    )}
                    title="Copy Space Number and DBA"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="h-4 w-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"
                      />
                    </svg>
                  </button>
                )}
                <div
                  className={classNames(
                    "text-primary-100 bg-primary-200/30 ring-primary-100",
                    "rounded-full flex-none py-1 px-2 text-xs font-medium ring-1 ring-inset"
                  )}
                >
                  {spaceInfo["spaceNumber"]}
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-5 w-5 flex-none text-sidebar-subtitle"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.25 4.5l7.5 7.5-7.5 7.5"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div
          className={`mt-1 text-xs leading-5 text-sidebar-subtitle flex-1 w-full flex flex-col items-start gap-x-2 overflow-hidden transition-all duration-300 h-auto ${
            isExpanded ? "max-h-32" : "max-h-0"
          }`}
        >
          {Object.keys(spaceInfo["summary"]).map((key: string) => (
            <div key={key} className="flex items-center break-words break-all">
              <b>{key}</b>: {spaceInfo["summary"][key]}
            </div>
          ))}
        </div>
      </div>
    </DisabledClickWrapper>
  );
});

const SpaceObjectList = () => {
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [visibleCount, setVisibleCount] = useState<number>(30);
  const {
    spaceObjects,
    selectedSpaceObjectID,
    setSelectedSpaceObjectID,
    disableFeature,
  } = useContext(CadviewerContext);

  // Get space selection state from Zustand store
  const { isSelectingSpaceForAI, spaceSelectedForAI } = useSpaceSelectionStore();

  // Check if we're in AI mode (either selecting or already selected a space for AI)
  const isInAIMode = isSelectingSpaceForAI || spaceSelectedForAI !== null;

  useEffect(() => {
    setVisibleCount(30);
  }, [searchKeyword, spaceObjects]);

  // Scroll to the select object item
  useEffect(() => {
    // Skip scroll behavior if we're in AI mode
    if (isInAIMode) {
      return;
    }

    if (selectedSpaceObjectID && spaceObjects) {
       const idx = spaceObjects.findIndex((s) => s.id.replaceAll(".", "_").replaceAll(" ", "_") === selectedSpaceObjectID);
       if (idx !== -1) {
          setTimeout(() => {
            startTransition(() => {
              setVisibleCount((prev) => Math.max(prev, idx + 20));
            });
          }, 50);
       }
    }

    setTimeout(() => {
      const element = document.getElementById(
        "space-object-" +
          `${selectedSpaceObjectID}`.replaceAll(".", "_").replaceAll(" ", "_")
      );
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "start",
        });
      }
    }, 400);
  }, [selectedSpaceObjectID, isInAIMode]);
  return (
    <div className="h-full flex flex-col  py-4">
      <form
        className="bg-white/30 p-2 m-2 rounded-md relative flex flex-1"
        action="#"
        method="GET"
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <label htmlFor="search-field" className="sr-only">
          Search
        </label>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="pointer-events-none absolute inset-y-0 left-2 h-full w-5 text-sidebar-title"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
          />
        </svg>

        <DisabledClickWrapper>
          <input
            id="search-field"
            className="bg-transparent block h-full w-full border-0 py-0 pl-8 pr-0 text-sidebar-title placeholder:text-sidebar-title/80 focus:ring-0 sm:text-sm outline-none  focus:outline-none"
            placeholder="Search..."
            type="text"
            name="search"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            disabled={disableFeature}
          />
        </DisabledClickWrapper>
        {searchKeyword && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            onClick={() => setSearchKeyword("")}
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="cursor-pointer absolute inset-y-0 right-2 h-full w-5 text-sidebar-title"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18 18 6M6 6l12 12"
            />
          </svg>
        )}
      </form>
      <div className="h-full overflow-y-hidden">
        <Scrollbar
          style={{ position: "relative" }}
          className="h-full"
          removeTracksWhenNotUsed={false}
          onScroll={(scrollValues: any) => {
            const scrollTop = scrollValues.scrollTop ?? scrollValues.currentTarget?.scrollTop;
            const scrollHeight = scrollValues.scrollHeight ?? scrollValues.currentTarget?.scrollHeight;
            const clientHeight = scrollValues.clientHeight ?? scrollValues.currentTarget?.clientHeight;
            
            if (scrollHeight !== undefined && scrollTop !== undefined && clientHeight !== undefined) {
              const itemsLength = (spaceObjects || []).length;
              if (scrollHeight - scrollTop <= clientHeight + 2000 && visibleCount < itemsLength) {
                setVisibleCount(Math.min(visibleCount + 30, itemsLength));
              }
            }
          }}
          thumbYProps={{
            renderer: (props) => {
              const { elementRef, ...restProps } = props;
              return (
                <div
                  {...restProps}
                  ref={elementRef}
                  className="ScrollbarsCustom-Thumb ScrollbarsCustom-ThumbY"
                  style={{ background: "white" }}
                />
              );
            },
          }}
        >
          {spaceObjects === undefined ? (
            <ul className="divide-y divide-white/5">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(
                (spaceObject) => (
                  <div
                    className="max-w-sm w-full mx-auto py-4"
                    key={spaceObject}
                  >
                    <div className="animate-pulse flex space-x-4">
                      <div className="flex-1 space-y-6 py-1">
                        <div className="space-y-3">
                          <div className="flex items-center gap-4">
                            <div className="bg-slate-700 rounded w-2 h-2 p-1"></div>
                            <div className="bg-slate-700 rounded h-2 w-2/5"></div>
                            <div className="flex-1" />
                            <div className="bg-slate-700 rounded-full h-5 w-1/6"></div>
                          </div>
                          <div className="h-2 bg-slate-700 rounded w-2/5"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
            </ul>
          ) : (
            <ul className="divide-y divide-white/5">
              {(spaceObjects ?? [])
                .filter((spaceObject: SpaceObjectProps) =>
                  searchKeyword.trim() === ""
                    ? true
                    : spaceObject.id
                        .toLowerCase()
                        .includes(searchKeyword.toLowerCase())
                )
                .slice(0, visibleCount)
                .map((spaceObject: SpaceObjectProps) => (
                  <SpaceObject
                    selected={
                      selectedSpaceObjectID ===
                      spaceObject.id.replaceAll(".", "_").replaceAll(" ", "_")
                    }
                    setSelectedSpaceObjectID={setSelectedSpaceObjectID}
                    key={spaceObject.id}
                    spaceObject={spaceObject}
                    disableFeature={disableFeature}
                  />
                ))}
            </ul>
          )}
        </Scrollbar>
      </div>
    </div>
  );
};

interface SpaceObjectCompProps {
  selected: boolean;
  setSelectedSpaceObjectID: (id: string | undefined) => void;
  spaceObject: SpaceObjectProps;
  disableFeature: boolean;
}

// Space object component
const SpaceObject = memo(({
  spaceObject,
  setSelectedSpaceObjectID,
  selected,
  disableFeature,
}: SpaceObjectCompProps) => {
  const { config } = useConfig();
  const focusSpaceObject = useCallback(() => {
    markLeftPaneNavigation(spaceObject.id.replaceAll(".", "_").replaceAll(" ", "_"));
    cadviewer.cvjs_hideOnlyPop();
    if (config.ShowInformativeBottomPanelWhenClickInLeftPanel) {
      setSelectedSpaceObjectID(spaceObject.id);
    }
    //cadviewer.cvjs_highlightRoomImmediate(spaceObject.id, true);
    cadviewer.cvjs_changeSpaceFixedLocation(
      spaceObject.id.replaceAll(".", "_"),
      null
    );

    // 9.44.2
    //	cadviewer.cvjs_zoomHere_ObjectId(spaceObject.id, config.ZoomFactor);
  }, [setSelectedSpaceObjectID, spaceObject.id, config.ShowInformativeBottomPanelWhenClickInLeftPanel]);

  return (
    <DisabledClickWrapper>
      <li
        key={spaceObject.id}
        id={"space-object-" + spaceObject.id}
        className={classNames(
          "relative flex items-center space-x-4 py-4 px-2",
          !disableFeature && "cursor-pointer hover:bg-sidebar-item-hover",
          "transition-colors duration-300 ease-in-out",
          // selected
          selected ? "bg-sidebar-item-hover" : ""
        )}
        onClick={() => {
          if (!disableFeature) {
            focusSpaceObject();
          }
        }}
      >
        <div className="min-w-0 flex-auto">
          <div className="flex items-center gap-x-3">
            <div
              className={classNames(
                selected
                  ? "text-sidebar-active-item bg-green-400/10"
                  : "text-sidebar-subtitle bg-gray-100/10",
                "flex-none rounded-full p-1"
              )}
            >
              <div className="h-2 w-2 rounded-full bg-current" />
            </div>
            <h2 className="min-w-0 text-sm font-semibold leading-6 text-sidebar-title">
              {/* <span className="truncate">{spaceObject.node}</span>
					<span className="text-sidebar-subtitle">/</span> */}
              <span className="whitespace-nowrap">{spaceObject.name}</span>
              <span className="absolute inset-0" />
            </h2>
          </div>
          <div className="mt-3 text-xs leading-5 text-sidebar-subtitle">
            <p
              className="flex-1 w-full"
              dangerouslySetInnerHTML={{
                __html: (spaceObject.type ?? "").replace("<br>", ""),
              }}
            />
          </div>
        </div>
        <div
          className={classNames(
            "text-primary-300 bg-primary-300/30 ring-primary-300",
            "rounded-full flex-none py-1 px-2 text-xs font-medium ring-1 ring-inset"
          )}
        >
          {spaceObject.id}
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="h-5 w-5 flex-none text-sidebar-subtitle"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 4.5l7.5 7.5-7.5 7.5"
          />
        </svg>
      </li>
    </DisabledClickWrapper>
  );
});

export default SideBarLeftPanel;
